/* eslint-disable */
import React, { useState, useRef, useEffect } from 'react';
import arrowGif from '../../../../../../assets/2025/arrow.gif';
import './index.less';
import './index.en.less';

const ArrowExpandIcon: React.FunctionComponent = () => (
  <svg viewBox="0 0 133 133" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="66.5" cy="66.5" r="65" stroke="currentColor" strokeWidth="3" />
    <path
      d="M38.8605 91.7725L93.3431 40.4304M91.7728 93.3428L93.3431 40.4304L40.4307 38.8602"
      stroke="currentColor"
      strokeWidth="6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowCollapseIcon: React.FunctionComponent = () => (
  <svg viewBox="0 0 133 133" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="66.5" cy="66.5" r="65" stroke="currentColor" strokeWidth="3" />
    <path
      d="M94.1395 41.2265L39.6569 92.5686M41.2272 39.6563L39.6569 92.5686L92.5693 94.1388"
      stroke="currentColor"
      strokeWidth="6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export interface IFlipCardItem {
  icon?: React.ReactNode;
  title: string;
  illustration?: React.ReactNode;
  content: string;
}

export interface IFlipCardGridProps {
  mainTitle?: string;
  subTitle?: string;
  description?: string;
  items: IFlipCardItem[];
  defaultActiveIndex?: number;
  id?: string;
  onComplete?: () => void;
  showGuideArrow?: boolean;
}

const FlipCardGrid: React.FunctionComponent<IFlipCardGridProps> = ({
  mainTitle,
  subTitle,
  description,
  items,
  defaultActiveIndex = -1,
  id,
  onComplete,
  showGuideArrow = true,
}) => {
  const [activeIndices, setActiveIndices] = useState<Set<number>>(
    () => (showGuideArrow ? new Set() : new Set(items.map((_, index) => index)))
  );
  const [completedIndices, setCompletedIndices] = useState<Set<number>>(new Set());
  const hasCompletedRef = useRef(false);

  useEffect(() => {
    setCompletedIndices(new Set());
    setActiveIndices(showGuideArrow ? new Set() : new Set(items.map((_, index) => index)));
    hasCompletedRef.current = false;
  }, [items.length]);

  useEffect(() => {
    if (
      items.length > 0 &&
      completedIndices.size === items.length &&
      !hasCompletedRef.current
    ) {
      hasCompletedRef.current = true;
      onComplete?.();
    }
  }, [completedIndices, items, onComplete]);

  const handleCardClick = (index: number) => {
    if (showGuideArrow) {
      const isCompleted = completedIndices.has(index);
      const isNextTarget = index === completedIndices.size;
      if (!isCompleted && !isNextTarget) return;
      if (isNextTarget) {
        const newCompleted = new Set(completedIndices);
        newCompleted.add(index);
        setCompletedIndices(newCompleted);
        setActiveIndices((prev) => {
          const next = new Set(prev);
          next.add(index);
          return next;
        });
      } else {
        setActiveIndices((prev) => {
          const next = new Set(prev);
          if (next.has(index)) next.delete(index);
          else next.add(index);
          return next;
        });
      }
      return;
    }
    setActiveIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
    if (onComplete) {
      onComplete();
    }
  };

  const arrowTargetIndex = showGuideArrow ? completedIndices.size : -1;

  return (
    <div className="flip-card-grid" id={id}>
      <div className="fcg-header">
        {mainTitle && <h2 className="fcg-main-title">{mainTitle}</h2>}
        {subTitle && <h3 className="fcg-sub-title">{subTitle}</h3>}
        {description && <p className="fcg-description">{description}</p>}
      </div>
      <div className="fcg-grid">
        {items.map((item, index) => {
          const isActive = activeIndices.has(index);
          const showArrow = showGuideArrow && index === arrowTargetIndex;
          return (
            <div
              key={index}
              className={`fcg-card ${isActive ? 'is-active' : ''}`}
            >
              <button
                className="fcg-card-toggle"
                onClick={() => handleCardClick(index)}
              >
                {isActive ? <ArrowCollapseIcon /> : <ArrowExpandIcon />}
                {showArrow && (
                  <img src={arrowGif} alt="" className="fcg-guide-arrow" />
                )}
              </button>
              <div className="fcg-card-icon">{item.icon}</div>
              <h4 className="fcg-card-title">{item.title}</h4>
              <div className="fcg-card-body">
                {isActive ? (
                  <div className="fcg-card-content">
                    <p>{item.content}</p>
                  </div>
                ) : (
                  <div className="fcg-card-illustration">
                    {item.illustration}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FlipCardGrid;
